import { getStringTag } from 'features/libra/helpers/getStringTag'
import { isCtpLayerId } from 'features/libra/stepComponents/ffs/ffsCtpMarkup'
import {
  FFS_INDICATION_LAYER_PREFIX,
  isIndicationLayerId,
} from 'features/libra/stepComponents/ffs/ffsIndicationMarkup'
import { FFS_LTA_PATHS_MARKUP_PATH } from 'features/libra/stepComponents/ffs/ffsLtaPathMarkup'
import { GeckoMarkupLayer, RolodexEntryType } from 'types'

/**
 * Groups FFS viz markup layers into the rows shown by the layer-visibility
 * control.
 *
 * A row can control MORE than one layer: each LTA is a single "Indications" row
 * that owns both its indication rect AND its flaw-boundary polygon (they are 1:1
 * -- the backend emits one flaw markup per LTA tagged `indication = lta_id`), so
 * toggling/hovering/zooming the LTA acts on both. CTPs are one layer per row.
 * Rulers are excluded (they keep the global `showRulers` scene toggle).
 *
 * Categorization is identity-based, not `data.path`: indication and CTP layers
 * are matched by id prefix; a flaw layer is a `FFS LTA Paths` layer that is
 * neither, folded into its indication via the `indication` tag.
 */

export type FfsLayerCategoryKey = 'indications' | 'ctps'

export interface FfsLayerRow {
  /** Stable row key. */
  key: string
  label: string
  /** Swatch color from the layer; may be absent (control falls back). */
  color: string | undefined
  /** Layers this row controls (visibility + hover highlight). */
  layerIds: string[]
  /** Layer whose bounds the camera frames when the row is clicked. */
  zoomLayer: GeckoMarkupLayer
}

export interface FfsLayerCategory {
  key: FfsLayerCategoryKey
  label: string
  rows: FfsLayerRow[]
}

export const FFS_LAYER_CATEGORY_LABELS: Record<FfsLayerCategoryKey, string> = {
  indications: 'Indications',
  ctps: 'Critical thickness profiles',
}

const isFlawPathLayer = (layer: GeckoMarkupLayer): boolean =>
  layer.data.path?.[0] === FFS_LTA_PATHS_MARKUP_PATH

const indicationTag = (layer: GeckoMarkupLayer): string | undefined =>
  getStringTag(layer.tags, RolodexEntryType.INDICATION)

/** One layer = one row (used for CTPs). */
const singleLayerRow = (layer: GeckoMarkupLayer): FfsLayerRow => ({
  key: layer.id,
  label: layer.data.name,
  color: layer.data.color,
  layerIds: [layer.id],
  zoomLayer: layer,
})

/** Returns the non-empty categories, in display order. */
export function categorizeFfsLayers(
  layers: ReadonlyArray<GeckoMarkupLayer>
): FfsLayerCategory[] {
  const indications: GeckoMarkupLayer[] = []
  const ctps: GeckoMarkupLayer[] = []
  const flawByIndicationId = new Map<string, GeckoMarkupLayer>()

  for (const layer of layers) {
    if (isIndicationLayerId(layer.id)) {
      indications.push(layer)
    } else if (isCtpLayerId(layer.id)) {
      ctps.push(layer)
    } else if (isFlawPathLayer(layer)) {
      const id = indicationTag(layer)
      if (id) flawByIndicationId.set(id, layer)
    }
  }

  // Each indication row folds in its 1:1 flaw-boundary layer, when present.
  const indicationRows: FfsLayerRow[] = indications.map((layer) => {
    const indicationId = layer.id.slice(FFS_INDICATION_LAYER_PREFIX.length)
    const flaw = flawByIndicationId.get(indicationId)
    return {
      key: indicationId,
      label: layer.data.name,
      color: layer.data.color,
      layerIds: flaw ? [layer.id, flaw.id] : [layer.id],
      zoomLayer: layer,
    }
  })

  const all: FfsLayerCategory[] = [
    {
      key: 'indications',
      label: FFS_LAYER_CATEGORY_LABELS.indications,
      rows: indicationRows,
    },
    {
      key: 'ctps',
      label: FFS_LAYER_CATEGORY_LABELS.ctps,
      rows: ctps.map(singleLayerRow),
    },
  ]
  return all.filter((c) => c.rows.length > 0)
}
